import { colors, intl, ui, UserId, UserProfile } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactElement } from 'react'
import { ParticipantOverview } from '../../model/participant-groups'
import KnowledgeCheckHeader from '../knowledge-check-header'
import CountRing from './count-ring'
import ParticipantsPanel from './participants-panel'
import StageCard from './stage-card'

const Summary = styled.div`
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: 24px 40px;
	justify-content: center;
	margin: auto;
`

const Caption = styled.div`
	color: ${colors.blackish};
	font-size: 28px;
	font-weight: 500;

	${ui.responsive.desktopFHD} {
		font-size: 36px;
	}
`

type Props = {
	userProfile: UserProfile | undefined | null
	title: string
	maxDurationInSeconds?: number
	startTime?: Date
	overview: ParticipantOverview
	profiles: Record<UserId, UserProfile>
	onFinish: () => void
	onShowResults: () => void
}

/**
 * The trainer's view of a knowledge check that is running, on the in-person
 * stage: how many have finished and sent in their answers, and who is where.
 * The online card is `KnowledgeCheckTrainerGridView`.
 */
const KnowledgeCheckTrainerStageView: FC<Props> = ({
	userProfile,
	title,
	maxDurationInSeconds,
	startTime,
	overview,
	profiles,
	onFinish,
	onShowResults,
}): ReactElement => {
	const t = intl.useTranslate()
	const { finished, total } = overview
	return (
		<StageCard
			header={
				<KnowledgeCheckHeader
					title={title}
					userProfile={userProfile}
					maxDurationInSeconds={maxDurationInSeconds}
					startTime={startTime}
				/>
			}
			sideLabel={t('knowledge_check.stage.participants')}
			side={<ParticipantsPanel groups={overview.groups} profiles={profiles} />}
			quietActions={
				<ui.ButtonMinor onClick={onFinish}>
					<intl.Translate>knowledge_check.stats.end</intl.Translate>
				</ui.ButtonMinor>
			}
			mainActions={
				<ui.ButtonPrimary onClick={onShowResults}>
					<intl.Translate>knowledge_check.stage.show_results</intl.Translate>
				</ui.ButtonPrimary>
			}
		>
			<Summary>
				<CountRing
					value={finished}
					max={total}
					label={t('knowledge_check.stage.finished_label', {
						finished,
						total,
					})}
				/>
				<Caption>
					<intl.Translate>
						knowledge_check.stage.finished_caption
					</intl.Translate>
				</Caption>
			</Summary>
		</StageCard>
	)
}

export default KnowledgeCheckTrainerStageView
